import { FormIds } from '@utils/enums/FormIds.enum';
import insuranceForm from '../forms/insurance';
import patientDetailsForm from '../forms/patientDetails';
import contactDetailsForm from '../forms/contactsDetails';
import useMergeForm from './useMergeForm';
import { FormBuilderObOptions } from '@components/FormBuilder/FormBuilder.ob';
import primaryInsuranceForm from '../forms/primaryInsurance';
import secondaryInsuranceForm from '../forms/secondaryInsurance';
import { usePatientStore } from '@store/patientStore';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { useMemo } from 'react';
import useCustomTranslation from './useCustomTranslation';
import { useOnlineBookingStore } from '@store/onlineBookingStore';

interface FormFieldsRequest {
  formIds: Array<FormIds>;
  patientIndex: number;
  optionsList?: Array<FormBuilderObOptions>;
}

const useGetFormFields = ({
  formIds,
  optionsList,
  patientIndex,
}: FormFieldsRequest) => {
  const { t } = useCustomTranslation();
  const {
    mergePatientDetails,
    createCustomFields,
    mergeContactsDetails,
    mergeCustomFieldsMarketing,
  } = useMergeForm();

  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const insuranceType = usePatientStore(state => state.patients)[patientIndex]
    ?.insurance?.type;

  const toReturn = useMemo(
    () =>
      formIds.map((formId, index) => {
        const options = optionsList ? optionsList[index] : null;
        switch (formId) {
          case FormIds.Insurance:
            return {
              data: insuranceForm(
                options && options.insurances ? options.insurances : [],
              ),
            };
          case FormIds.PatientDetail:
            return {
              data: mergePatientDetails(
                patientDetailsForm(
                  t,
                  options &&
                    options.countryList &&
                    options.countryStatesList &&
                    options.genderList
                    ? {
                        countryList: options.countryList,
                        countryStatesList: options.countryStatesList,
                        genderList: options.genderList,
                      }
                    : undefined,
                  !!brandName,
                ),
              ),
            };
          case FormIds.PatientDetailPrimaryInsurance:
            return {
              data: mergePatientDetails(
                primaryInsuranceForm(
                  t,
                  //"Other" insurance options is considered as Vision, not Medical */}
                  [
                    StaticValues.INSURANCE_TYPE_VISION,
                    StaticValues.INSURANCE_TYPE_OTHER,
                  ].includes(insuranceType as StaticValues),
                  options &&
                    options.countryList &&
                    options.countryStatesList &&
                    options.insurances &&
                    options.genderList &&
                    options.holders
                    ? {
                        insurances: options.insurances,
                        countryList: options.countryList,
                        countryStatesList: options.countryStatesList,
                        genderList: options.genderList,
                        holders: options.holders,
                      }
                    : undefined,
                ),
              ),
            };
          case FormIds.PatientDetailSecondaryInsurance:
            return {
              data: mergePatientDetails(
                secondaryInsuranceForm(
                  t,
                  insuranceType === StaticValues.INSURANCE_TYPE_MEDICAL,
                  options &&
                    options.countryList &&
                    options.countryStatesList &&
                    options.insurances &&
                    options.genderList &&
                    options.holders
                    ? {
                        insurances: options.insurances,
                        countryList: options.countryList,
                        countryStatesList: options.countryStatesList,
                        genderList: options.genderList,
                        holders: options.holders,
                      }
                    : undefined,
                ),
              ),
            };
          case FormIds.CustomPage:
            return {
              data: createCustomFields('storeCustomFieldsCustomPage', formId),
            };
          case FormIds.CustomFieldsPatient:
            return {
              data: createCustomFields(
                'patientDetails.storeCustomFieldsPatient',
                formId,
                true,
              ),
            };
          case FormIds.CustomFieldsInsurance:
            return {
              data: createCustomFields(
                'patientDetails.storeCustomFieldsInsurance',
                formId,
                true,
              ),
            };
          case FormIds.ContactInformation:
            return {
              data: mergeContactsDetails(contactDetailsForm(t)),
            };
          case FormIds.CustomFieldsMarketing:
            return {
              data: mergeCustomFieldsMarketing([], !!brandName),
            };
          default:
            return {
              data: [],
            };
        }
      }),
    [formConfigs],
  );
  return toReturn;
};

export default useGetFormFields;
